import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { rawFetch } from "@/lib/api/client";
import { useApp } from "@/lib/store";
import { useWorkspacePermissions } from "@/lib/workspacePermissions";
import { ErrorBox, PageHeader, StatusPill } from "@/components/common";
import { Button, Checkbox, Field, SectionCard } from "@/components/ui-kit";
import { useTermSets } from "@/components/TermSetSelector";

type Modality = "text" | "record" | "event" | "image" | "audio" | "video" | "other";
type Purpose = "inspect" | "contextualize" | "action_evidence";
type FieldRule = { name: string; selector: string; required: boolean; ontology_property_iri?: string };
type Definition = { purpose: Purpose; modalities: Modality[]; fields: FieldRule[]; require_ontology: boolean; target_class_iri: string;
  context_defaults?: Record<string, string[]> };
type Profile = { id: string; name: string; current_version: number; published_version: number | null;
  digest: string; published_digest: string; definition?: Definition };
type OntologyVersion = { id: string; semver: string; status: string };
type Result = { receipt_id: string; status: string; reasons: string[];
  input: { modality: string; byte_count: number; sha256: string };
  assertions: Array<{ name: string; value: unknown; state: string; locator: string }>;
  checks: Record<string, string> };

const modalities: Modality[] = ["text", "record", "event", "image", "audio", "video", "other"];
const blank: Definition = { purpose: "inspect", modalities: ["text", "record", "event", "image"],
  fields: [{ name: "modality", selector: "metadata.modality", required: true },
    { name: "size", selector: "metadata.byte_count", required: true }], require_ontology: false, target_class_iri: "" };
const inputClass = "w-full border border-border bg-panel px-3 py-2 text-sm text-fg";

export function MetadataProfiles() {
  const workspace = useApp((state) => state.workspaceId);
  const { canEdit, canAdmin } = useWorkspacePermissions();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [definition, setDefinition] = useState<Definition>(blank);
  const [seedTermSetVersionId, setSeedTermSetVersionId] = useState("");
  const [modality, setModality] = useState<Modality>("record");
  const [sample, setSample] = useState('{"status":"synthetic","count":3}');
  const [file, setFile] = useState<File | null>(null);
  const [ontologyId, setOntologyId] = useState("");
  const [result, setResult] = useState<Result | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const termSets = useTermSets(workspace);
  const ontologyVersions = useQuery<{ results: OntologyVersion[] }>({
    queryKey: ["metadata-ontology-versions", workspace],
    queryFn: () => rawFetch(`/ontology/versions/?workspace=${encodeURIComponent(workspace || "")}&page_size=100`),
    enabled: Boolean(workspace),
  });
  const list = useQuery<Profile[]>({
    queryKey: ["metadata-profiles", workspace],
    queryFn: () => rawFetch(`/pipelines/metadata-profiles/?workspace=${workspace}`),
    enabled: !!workspace,
  });
  const detail = useQuery<Profile>({
    queryKey: ["metadata-profile", selected],
    queryFn: () => rawFetch(`/pipelines/metadata-profiles/${selected}/`),
    enabled: !!selected,
  });
  useEffect(() => {
    if (!detail.data?.definition) return;
    setName(detail.data.name);
    setDefinition(detail.data.definition);
    setSeedTermSetVersionId("");
    setResult(null);
  }, [detail.data]);

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["metadata-profiles", workspace] });
    await queryClient.invalidateQueries({ queryKey: ["metadata-profile", selected] });
  };
  const act = async (task: () => Promise<void>) => {
    setError(null);
    setBusy(true);
    try { await task(); } catch (cause) { setError(cause); } finally { setBusy(false); }
  };
  const save = () => act(async () => {
    if (selected && !detail.data) throw new Error("Profile is still loading.");
    const payload = { name: name.trim(), definition, workspace,
      ...(seedTermSetVersionId ? { term_set_version_id: seedTermSetVersionId } : {}) };
    if (selected && detail.data) {
      await rawFetch(`/pipelines/metadata-profiles/${selected}/`, {
        method: "PUT", body: JSON.stringify({ ...payload, expected_version: detail.data.current_version }),
      });
    } else {
      const created = await rawFetch<Profile>("/pipelines/metadata-profiles/", {
        method: "POST", body: JSON.stringify(payload),
      });
      setSelected(created.id);
    }
    await refresh();
  });
  const publish = () => act(async () => {
    if (!selected || !detail.data) return;
    await rawFetch(`/pipelines/metadata-profiles/${selected}/publish/`, {
      method: "POST", body: JSON.stringify({ expected_version: detail.data.current_version }),
    });
    await refresh();
  });
  const evaluate = () => act(async () => {
    if (!selected || !detail.data?.published_digest) return;
    let envelope: Record<string, unknown>;
    if (modality === "text") envelope = { modality, payload: sample, media_type: "text/plain" };
    else if (modality === "record" || modality === "event") {
      const payload: unknown = JSON.parse(sample);
      if (!payload || Array.isArray(payload) || typeof payload !== "object") {
        throw new Error("Record and event inputs require a JSON object.");
      }
      envelope = { modality, payload, media_type: "application/json" };
    } else {
      if (!file) throw new Error("Select a small synthetic media file.");
      if (file.size > 50_000) throw new Error("Input must be at most 50,000 bytes.");
      const bytes = new Uint8Array(await file.arrayBuffer());
      let binary = "";
      for (const byte of bytes) binary += String.fromCharCode(byte);
      envelope = { modality, payload_base64: btoa(binary), media_type: file.type || undefined };
    }
    const response = await rawFetch<Result>(`/pipelines/metadata-profiles/${selected}/evaluate/`, {
      method: "POST", body: JSON.stringify({ profile_digest: detail.data.published_digest,
        ontology_version_id: ontologyId || undefined, envelope }),
    });
    setResult(response);
  });
  const updateRule = (index: number, patch: Partial<FieldRule>) => setDefinition((current) => ({
    ...current, fields: current.fields.map((field, i) => i === index ? { ...field, ...patch } : field),
  }));
  return <div className="space-y-5 pb-10">
    <PageHeader title="Metadata Profiles" subtitle="Define which facts an incoming file, event or record should provide." />
    <SectionCard title="What is a metadata profile?" summary="A reusable checklist for incoming content.">
      <p className="text-sm text-muted">Attach a published profile to a Catalog source to supply default context such as line of business, product, jurisdiction and language. Retrieval can use that context to narrow document results. Evaluating a sample shows what was found; it does not authenticate the source or run an agent.</p>
    </SectionCard>
    <p className="border-l-2 border-accent bg-panel px-4 py-3 text-sm text-muted">
      Input stays transient in this evaluation. The saved receipt contains measurements, hashes and redacted assertions.
      Source authenticity and content analysis are marked not assessed until a trusted adapter supplies them.
    </p>
    {list.error && <ErrorBox error={list.error} />}{detail.error && <ErrorBox error={detail.error} />}
    {error != null && <ErrorBox error={error} />}
    {!workspace ? <p className="text-sm text-muted">Select a workspace to continue.</p> : <div className="grid gap-5 xl:grid-cols-[17rem_minmax(0,1fr)]">
      <section className="card space-y-3">
        <div className="flex items-center justify-between gap-2"><h2 className="font-medium">Profile library</h2>
          {canEdit && <Button onClick={() => { setSelected(null); setName(""); setDefinition(blank); setResult(null); }}>New</Button>}</div>
        {list.isLoading && <p className="text-sm text-muted">Loading profiles…</p>}
        {list.data?.length === 0 && <p className="text-sm text-muted">No profiles in this workspace.</p>}
        <div className="space-y-1">{list.data?.map((profile) => <button key={profile.id} onClick={() => setSelected(profile.id)}
          className={`w-full border px-3 py-2 text-left text-sm ${selected === profile.id ? "border-accent bg-accent/10" : "border-border bg-panel"}`}>
          <span className="block font-medium">{profile.name}</span><span className="text-xs text-muted">Version {profile.current_version} · {profile.published_version ? `Published ${profile.published_version}` : "Draft"}</span>
        </button>)}</div>
      </section>
      <div className="space-y-5">
        <section className="card space-y-4"><div className="flex flex-wrap items-center justify-between gap-2"><h2 className="font-medium">Profile definition</h2>
          {detail.data && <span className="text-xs text-muted">Version {detail.data.current_version}</span>}</div>
          <Field label="Name" hint={selected ? "The name stays with this version; save changes as a new version." : "Choose a name your team will recognize."}>
            <input className={inputClass} value={name} onChange={(event) => setName(event.target.value)} disabled={!canEdit || !!selected} maxLength={160} />
          </Field>
          <div className="grid gap-3 sm:grid-cols-2"><label className="text-sm">Purpose<select className={inputClass} value={definition.purpose} disabled={!canEdit}
            onChange={(event) => setDefinition({ ...definition, purpose: event.target.value as Purpose, require_ontology: event.target.value !== "inspect" })}>
            <option value="inspect">Inspect</option><option value="contextualize">Contextualize</option><option value="action_evidence">Action evidence</option></select></label>
            <label className="flex items-end gap-2 pb-2 text-sm"><Checkbox checked={definition.require_ontology} disabled={!canEdit}
              onChange={(event) => setDefinition({ ...definition, require_ontology: event.target.checked })} /> Require pinned ontology</label></div>
          {definition.purpose !== "inspect" && <label className="block text-sm">Ontology target class IRI<input className={inputClass} value={definition.target_class_iri || ""}
            onChange={(event) => setDefinition({ ...definition, target_class_iri: event.target.value })} disabled={!canEdit}
            placeholder="https://example.org/Asset" /></label>}
          <label className="block text-sm">Seed context from approved term-set version (optional)
            <select className={inputClass} value={seedTermSetVersionId} disabled={!canEdit}
              onChange={(event) => setSeedTermSetVersionId(event.target.value)}>
              <option value="">Keep current context</option>
              {termSets.data?.results.flatMap((set) => set.versions.map((version) =>
                <option key={version.id} value={version.id}>{set.name} · v{version.number} · {version.entry_count} terms</option>))}
            </select>
            <span className="text-xs text-muted">Seeds LOB, product, business unit, jurisdiction, language and data entity. The published profile version is pinned on each Catalog source.</span>
          </label>
          {definition.context_defaults && Object.values(definition.context_defaults).some((values) => values.length) &&
            <p className="border-l-2 border-accent px-3 text-xs text-muted">Current context: {Object.entries(definition.context_defaults)
              .filter(([, values]) => values.length).map(([key, values]) => `${key}: ${values.join(", ")}`).join(" · ")}</p>}
          <fieldset><legend className="mb-2 text-sm">Applicable input types</legend><div className="flex flex-wrap gap-x-5 gap-y-2">{modalities.map((kind) =>
            <label className="flex items-center gap-2 text-sm capitalize" key={kind}><Checkbox disabled={!canEdit}
              checked={definition.modalities.includes(kind)} onChange={(event) => setDefinition({ ...definition,
                modalities: event.target.checked ? [...definition.modalities, kind] : definition.modalities.filter((item) => item !== kind) })} />{kind}</label>)}</div></fieldset>
          <div className="space-y-2"><div className="flex items-center justify-between"><h3 className="text-sm font-medium">Field rules</h3>
            {canEdit && <Button onClick={() => setDefinition({ ...definition, fields: [...definition.fields,
              { name: "", selector: "metadata.modality", required: false }] })}>Add field</Button>}</div>
            <p className="text-xs text-muted">Selectors: metadata.modality, metadata.byte_count, metadata.media_type, metadata.sha256, record.field_name or text.contains:literal.</p>
            {definition.fields.map((field, index) => <div key={index} className="grid gap-2 border border-border p-2 sm:grid-cols-[1fr_1.6fr_auto_auto]">
              <input aria-label={`Field ${index + 1} name`} className={inputClass} value={field.name} disabled={!canEdit} placeholder="Field name" onChange={(event) => updateRule(index, { name: event.target.value })} />
              <input aria-label={`Field ${index + 1} selector`} className={inputClass} value={field.selector} disabled={!canEdit} placeholder="Selector" onChange={(event) => updateRule(index, { selector: event.target.value })} />
              <label className="flex items-center gap-1 text-xs"><Checkbox checked={field.required} disabled={!canEdit} onChange={(event) => updateRule(index, { required: event.target.checked })} />Required</label>
              {canEdit && <Button variant="danger" aria-label={`Remove field ${index + 1}`} onClick={() => setDefinition({ ...definition, fields: definition.fields.filter((_, i) => i !== index) })}>Remove</Button>}
              {definition.purpose !== "inspect" && <input aria-label={`Field ${index + 1} ontology property IRI`} className={`${inputClass} sm:col-span-4`}
                value={field.ontology_property_iri || ""} disabled={!canEdit} placeholder="Ontology property IRI (optional)"
                onChange={(event) => updateRule(index, { ontology_property_iri: event.target.value })} />}
            </div>)}</div>
          {canEdit && <div className="flex flex-wrap gap-2"><Button variant="primary" disabled={busy || !name.trim() || (!!selected && !detail.data)} onClick={save}>{selected ? "Save new version" : "Create draft"}</Button>
            {selected && canAdmin && <Button disabled={busy || !detail.data} onClick={publish}>Publish current version</Button>}</div>}
        </section>
        {selected && <section className="card space-y-4"><div className="flex items-center justify-between"><h2 className="font-medium">Input simulator and run inspector</h2>
          <StatusPill status={result?.status ?? (detail.data?.published_version ? "ready" : "draft")} /></div>
          <p className="text-xs text-muted">Synthetic inputs only. No model call, source authentication, media classifier or action execution.</p>
          <div className="grid gap-3 sm:grid-cols-2"><label className="text-sm">Input type<select className={inputClass} value={modality} onChange={(event) => { setModality(event.target.value as Modality); setResult(null); }}>
            {modalities.map((kind) => <option value={kind} key={kind}>{kind}</option>)}</select></label>
            <label className="text-sm">Ontology version (optional)<select className={inputClass} value={ontologyId} onChange={(event) => setOntologyId(event.target.value)}>
              <option value="">No ontology context</option>{ontologyVersions.data?.results.map((version) =>
                <option key={version.id} value={version.id}>Version {version.semver} · {version.status}</option>)}
            </select></label></div>
          {modality === "text" || modality === "record" || modality === "event" ? <label className="block text-sm">Transient input<textarea className={`${inputClass} min-h-28 font-mono`} value={sample} onChange={(event) => setSample(event.target.value)} maxLength={50_000} /></label> :
            <label className="block text-sm">Transient media file<input className={inputClass} type="file" onChange={(event) => setFile(event.target.files?.[0] ?? null)} /></label>}
          <Button variant="primary" onClick={evaluate} disabled={busy || !canEdit || !detail.data?.published_version}>Evaluate transient input</Button>
          {result && <div className="space-y-3 border-t border-border pt-4 text-sm" role="status"><div className="flex flex-wrap gap-3"><StatusPill status={result.status} />
            <span>{result.input.modality} · {result.input.byte_count} bytes</span></div>
            {result.reasons.length > 0 && <p>Reasons: {result.reasons.join(", ")}</p>}
            <div className="grid gap-2 sm:grid-cols-2">{result.assertions.map((assertion) => <div key={assertion.name} className="border border-border p-3">
              <div className="font-medium">{assertion.name}</div><div className="break-all text-muted">{assertion.state}: {String(assertion.value ?? "—")}</div>
              </div>)}</div>
            <p className="text-xs text-muted">Source authenticity: {result.checks.source_authenticity}; shape: {result.checks.shape}. This result is not an action authorization.</p>
          </div>}
        </section>}
      </div>
    </div>}
  </div>;
}
